import { SetStateAction, useEffect, useState } from "react";
import { ModalWithButtons } from "./modal";
import { BlackButton, GreyButton } from "../button";
import { useTranslation } from "react-i18next";
import { NeutralColor } from "node-library/dist/colors";
import {
	FrontendBatchPageTestBatchDetails,
	FrontendTestResult,
} from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import { CommonSwitch, CustomTextInput } from "../inputs";
import { Severity } from "node-library/dist/constants";
import { GreenTickIcon } from "../toast/successIcons";
import { useSnackbar } from "../toast/showToastMessage";
import { FrontendUserInfo } from "node-library/dist/portal";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { RedErrorIcon } from "../toast/errorIcons";
import CommonAutoCompleteDropdown from "../commonAutoCompleteDropdown";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<SetStateAction<boolean>>;
	batchDetails: FrontendBatchPageTestBatchDetails;
	setBatchDetails: React.Dispatch<
		React.SetStateAction<FrontendBatchPageTestBatchDetails | undefined>
	>;
}

export default function EditAssignedUsersModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [loading, setLoading] = useState<boolean>(false);
	const [analysts, setAnalysts] = useState<Array<FrontendUserInfo>>([]); //excluding self

	const [assignedAnalysts, setAssignedAnalysts] = useState<
		Array<{ title: string; id: string }>
	>([]);

	async function getAnalysts() {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/sample-test-batches/get-batch-permitted-analysts",
				{
					batchTypeId: props.batchDetails.batchTypeId,
					otherTestCategoryId: props.batchDetails.mainTestCategoryId,
					isOtherTestBatch: props.batchDetails.isOtherTestBatch,
				},
			);

			const analysts: Array<FrontendUserInfo> = response?.data?.analysts;
			setAnalysts(analysts);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	async function handleUpdateAssignedUsers() {
		setLoading(true);
		try {
			const assignedAnalystIds: Array<string> = assignedAnalysts.map(
				(analyst) => {
					return analyst.id;
				},
			);
			const assignedAnalystUsers: Array<FrontendUserInfo> = analysts.filter(
				(analyst) => {
					return assignedAnalystIds.includes(analyst.userId);
				},
			);
			const response = await sendPOSTRequest(
				"/sample-test-batches/edit-batch-users",
				{
					assignedAnalystIds: assignedAnalystIds,
					batchId: props.batchDetails.testBatchId,
				},
			);

			props.setBatchDetails((prevBatch) => {
				return prevBatch
					? { ...prevBatch, assignedUsers: assignedAnalystUsers }
					: undefined;
			});
			openSnackbar(
				translation("UserUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			props.setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (props.open) {
			getAnalysts();
			setAssignedAnalysts(
				props.batchDetails.assignedUsers.map((user) => {
					return {
						title: `${user?.firstName} ${user?.lastName}`,
						id: user.userId,
					};
				}),
			);
		}
	}, [props.open]);

	let analystOptions = analysts.map((analyst: FrontendUserInfo) => {
		return {
			title: `${analyst.firstName} ${analyst.lastName}`,
			id: analyst.userId,
		};
	});

	const selectAssignedAnalystDropdown: React.ReactNode = (
		<CommonAutoCompleteDropdown
			id="select-assigned-analyst-dropdown"
			label={translation("AssignTo")}
			options={analystOptions}
			value={assignedAnalysts}
			setValue={(selected: Array<{ title: string; id: string }>) => {
				// Remove duplicates by id
				const unique = Array.from(
					new Map(selected.map((a) => [a.id, a])).values(),
				);
				setAssignedAnalysts(unique);
			}}
			disabled={loading}
			multiple={true}
			getOptionLabel={(option) => option.title}
		/>
	);

	const modal: React.ReactNode = (
		<ModalWithButtons
			headerText={translation("AssignUsers")}
			// headerDescription={translation("UploadfilesDescription")}
			open={props.open}
			setOpen={props.setOpen}
			modalWidth={500}
			buttons={[
				<GreyButton
					key="delete-sample-cancel-button"
					id="delete-sample-cancel-button"
					children={translation("Cancel")}
					onClick={() => {
						props.setOpen(false);
					}}
				></GreyButton>,
				<BlackButton
					key="delete-sample-proceed-button"
					id="delete-sample-proceed-button"
					children={translation("Confirm")}
					onClick={() => {
						handleUpdateAssignedUsers();
					}}
				></BlackButton>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "5px",
				}}
			>
				{selectAssignedAnalystDropdown}
			</div>
		</ModalWithButtons>
	);

	return modal;
}
